import React from 'react';
import { FloorPlanViewerProps } from '@/types/floorPlanViewer';
import { FloorPlanProvider, useFloorPlanContext } from './FloorPlanProvider';
import { FloorPlanHeader } from './components/FloorPlanHeader';
import { FloorPlanToolbar } from './components/FloorPlanToolbar';
import { FloorPlanViewport } from './components/FloorPlanViewport';
import { FloorPlanDrawers } from './components/FloorPlanDrawers';
import { FloorPlanErrorBoundary } from './components/ErrorBoundary';
import { LeftToolbar } from './components/LeftToolbar';
import {
  useRealtimeCollaboration,
  RemoteDragState,
  RemoteCursor,
  RemoteSelection,
} from '@/hooks/useRealtimeCollaboration';
import { CircuitCreationHandler } from './components/CircuitCreationHandler';
import { CircuitCreationHandlerOptimistic } from './components/CircuitCreationHandlerOptimistic';
import { featureFlags } from '@/utils/featureFlags';
import { PendingOperationsIndicator } from './components/PendingOperationsIndicator';
import { LoadsView } from './components/LoadsView';
import { useFloorPlanSetup } from './hooks/useFloorPlanSetup';
import { AnnotationContextualToolbar } from './components/AnnotationContextualToolbar';
import { Loader2 } from 'lucide-react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useAppStore, useSelectedRooms, useSelectedAnnotations } from '@/stores/appStore';
import { useMediaQuery } from '@/hooks/use-mobile';
import { logger } from '@/lib/logger';
import { useProjectIconStyle } from '@/hooks/useProjectIconStyle';
import { useUndoRedoStore } from '@/stores/undoRedoStore';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { useScaleOnboarding } from '@/hooks/useScaleOnboarding';
import { RemoteDragIndicator } from './RemoteDragIndicator';
import { RemoteCursorIndicator } from './RemoteCursorIndicator';
import { RemoteSelectionIndicator } from './RemoteSelectionIndicator';
import { UnifiedPlacementPalette } from './components/UnifiedPlacementPalette';
import { PlacementModeIndicator } from './components/PlacementModeIndicator';
import { useUnifiedPlacement, type PlacementItem } from './hooks/useUnifiedPlacement';
import { ProjectReports } from '@/components/reports';
import { AIProductCreationModal } from '@/features/product-library/components/AIProductCreationModal';

export interface FloorPlanViewerV2Props extends FloorPlanViewerProps {
  hideHeader?: boolean;
  viewMode?: 'v2' | 'original' | 'sideBySide';
  onViewModeChange?: (mode: 'v2' | 'original' | 'sideBySide') => void;
}

// Component to handle realtime collaboration callbacks with access to floor plan context
function RealtimeCollaborationHandler({
  floorPlanId,
  isPublicView,
  onActiveUsersChange,
  onPortalMappingUpdate,
  onBroadcastFunctionsReady,
  onRemoteDragsChange,
  onRemoteCursorsChange,
  onRemoteSelectionsChange,
}: {
  floorPlanId: string;
  isPublicView?: boolean;
  onActiveUsersChange?: (users: any[]) => void;
  onPortalMappingUpdate?: (payload: any) => void;
  onBroadcastFunctionsReady?: (functions: {
    broadcastDrag: (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementId: string,
      x: number,
      y: number,
    ) => Promise<void>;
    broadcastDragEnd: (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementId: string,
    ) => Promise<void>;
    broadcastCursor: (x: number, y: number) => Promise<void>;
    broadcastCursorLeave: () => Promise<void>;
    broadcastSelection: (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementIds: string[],
    ) => Promise<void>;
  }) => void;
  onRemoteDragsChange?: (remoteDrags: Map<string, RemoteDragState>) => void;
  onRemoteCursorsChange?: (remoteCursors: Map<string, RemoteCursor>) => void;
  onRemoteSelectionsChange?: (remoteSelections: Map<string, RemoteSelection>) => void;
}) {
  const { data } = useFloorPlanContext();
  const { refetchIndividual } = data;

  // Memoize callbacks to prevent re-subscriptions
  const handleFixtureUpdate = useCallback(() => {
    logger.log('[Realtime] Fixture updated by another user - refetching');
    refetchIndividual.fixtures?.();
  }, [refetchIndividual]);

  const handleCircuitUpdate = useCallback(() => {
    logger.log('[Realtime] Circuit updated by another user - refetching');
    refetchIndividual.circuits?.();
  }, [refetchIndividual]);

  const handleControlUpdate = useCallback(() => {
    logger.log('[Realtime] Control updated by another user - refetching');
    refetchIndividual.controls?.();
  }, [refetchIndividual]);

  const handleProductUpdate = useCallback(() => {
    logger.log('[Realtime] Product updated by another user - refetching');
    refetchIndividual.products?.();
  }, [refetchIndividual]);

  const handleAnnotationUpdate = useCallback(() => {
    logger.log('[Realtime] Annotation updated by another user - refetching');
    refetchIndividual.annotations?.();
  }, [refetchIndividual]);

  const handleRoomUpdate = useCallback(() => {
    logger.log('[Realtime] Room updated by another user - refetching');
    refetchIndividual.rooms?.();
  }, [refetchIndividual]);

  const handlePanelUpdate = useCallback(() => {
    logger.log('[Realtime] Panel updated by another user - refetching');
    refetchIndividual.panels?.();
  }, [refetchIndividual]);

  // Set up realtime collaboration with refetch callbacks
  const {
    activeUsers,
    broadcastDrag,
    broadcastDragEnd,
    broadcastCursor,
    broadcastCursorLeave,
    broadcastSelection,
    remoteDrags,
    remoteCursors,
    remoteSelections,
  } = useRealtimeCollaboration({
    floorPlanId,
    isPublicView,
    onFixtureUpdate: handleFixtureUpdate,
    onCircuitUpdate: handleCircuitUpdate,
    onControlUpdate: handleControlUpdate,
    onProductUpdate: handleProductUpdate,
    onAnnotationUpdate: handleAnnotationUpdate,
    onRoomUpdate: handleRoomUpdate,
    onPanelUpdate: handlePanelUpdate,
    onPortalMappingUpdate,
  });

  // Notify parent about broadcast functions being ready
  useEffect(() => {
    if (onBroadcastFunctionsReady) {
      onBroadcastFunctionsReady({
        broadcastDrag,
        broadcastDragEnd,
        broadcastCursor,
        broadcastCursorLeave,
        broadcastSelection,
      });
    }
  }, [
    broadcastDrag,
    broadcastDragEnd,
    broadcastCursor,
    broadcastCursorLeave,
    broadcastSelection,
    onBroadcastFunctionsReady,
  ]);

  // Notify parent about active users changes
  useEffect(() => {
    if (onActiveUsersChange) {
      onActiveUsersChange(activeUsers);
    }
  }, [activeUsers, onActiveUsersChange]);

  // Notify parent about remote drags changes
  useEffect(() => {
    if (onRemoteDragsChange) {
      onRemoteDragsChange(remoteDrags);
    }
  }, [remoteDrags, onRemoteDragsChange]);

  // Notify parent about remote cursors changes
  useEffect(() => {
    if (onRemoteCursorsChange) {
      onRemoteCursorsChange(remoteCursors);
    }
  }, [remoteCursors, onRemoteCursorsChange]);

  // Notify parent about remote selections changes
  useEffect(() => {
    if (onRemoteSelectionsChange) {
      onRemoteSelectionsChange(remoteSelections);
    }
  }, [remoteSelections, onRemoteSelectionsChange]);

  // This component doesn't render anything - it just handles realtime callbacks
  return null;
}

// Component to handle LoadsView with access to floor plan context
function LoadsViewWithContext({
  onTabChange,
}: {
  onTabChange: (tab: 'floorPlan' | 'loads' | 'reports') => void;
}) {
  const { data } = useFloorPlanContext();
  const { refetch } = data;

  const handleViewCircuit = useCallback(
    (circuitId: string) => {
      // Switch back to floor plan tab
      onTabChange('floorPlan');

      // Wait for tab switch to complete
      setTimeout(() => {
        // Get the app store state
        const state = useAppStore.getState();

        // Select the circuit (this will trigger the blinking effect)
        state.selectEntity('circuit', circuitId);

        // Get fixtures for this circuit
        const circuitFixtures = data.fixtures.filter(f => f.circuit_id === circuitId);

        // Center and zoom on the circuit
        if (circuitFixtures.length > 0) {
          // Calculate bounding box
          const minX = Math.min(...circuitFixtures.map(f => f.x_position));
          const maxX = Math.max(...circuitFixtures.map(f => f.x_position));
          const minY = Math.min(...circuitFixtures.map(f => f.y_position));
          const maxY = Math.max(...circuitFixtures.map(f => f.y_position));

          const bboxW = maxX - minX || 1;
          const bboxH = maxY - minY || 1;

          // Get viewport element
          const viewportEl = document.querySelector('.floor-plan-viewport');
          if (viewportEl) {
            const rect = viewportEl.getBoundingClientRect();
            const viewportW = rect.width;
            const viewportH = rect.height;

            // Calculate zoom to fit with padding
            const padding = 0.1; // 10% padding
            const fitFactor = 1 - padding * 2;
            const zoomForWidth = (viewportW * fitFactor) / bboxW;
            const zoomForHeight = (viewportH * fitFactor) / bboxH;
            const targetScale = Math.min(zoomForWidth, zoomForHeight, 2); // Max zoom of 2

            // Calculate center position
            const centerX = (minX + maxX) / 2;
            const centerY = (minY + maxY) / 2;

            const targetX = viewportW / 2 - centerX * targetScale;
            const targetY = viewportH / 2 - centerY * targetScale;

            // Animate viewport
            const startTime = performance.now();
            const duration = 400;
            const startViewport = state.floorPlan.viewport;

            const animate = (currentTime: number) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);

              // Ease-in-out
              const easeProgress =
                progress < 0.5
                  ? 4 * progress * progress * progress
                  : 1 - Math.pow(-2 * progress + 2, 3) / 2;

              const scale =
                startViewport.scale + (targetScale - startViewport.scale) * easeProgress;
              const positionX =
                startViewport.positionX + (targetX - startViewport.positionX) * easeProgress;
              const positionY =
                startViewport.positionY + (targetY - startViewport.positionY) * easeProgress;

              state.updateViewport({ scale, positionX, positionY });

              if (progress < 1) {
                requestAnimationFrame(animate);
              }
            };

            requestAnimationFrame(animate);
          }
        }
      }, 100);
    },
    [data.fixtures, onTabChange],
  );

  const handleDeleteCircuit = useCallback(
    async (circuitId: string) => {
      try {
        const { deleteCircuit } = await import('@/services/floorPlanService');
        const { success } = await deleteCircuit(circuitId);
        if (success) {
          await refetch();
        }
        return success;
      } catch (error) {
        logger.error('Failed to delete circuit:', error);
        return false;
      }
    },
    [refetch],
  );

  return (
    <LoadsView
      className="h-full bg-gray-50"
      onViewCircuit={handleViewCircuit}
      onDeleteCircuit={handleDeleteCircuit}
    />
  );
}

// Component to handle AnnotationContextualToolbar with access to floor plan context
function AnnotationToolbarWithContext({
  floorPlanId,
  selectedAnnotations,
}: {
  floorPlanId: string;
  selectedAnnotations: Set<string>;
}) {
  return (
    <AnnotationContextualToolbar
      floorPlanId={floorPlanId}
      selectedAnnotations={selectedAnnotations}
    />
  );
}

// Component to handle LeftToolbar with access to floor plan context
function LeftToolbarWithContext(props: any) {
  const { data } = useFloorPlanContext();
  const hasRooms = data?.rooms && data.rooms.length > 0;

  return <LeftToolbar {...props} hasRooms={hasRooms} />;
}

/**
 * FloorPlanViewerV2 - Decomposed version of the floor plan viewer
 * This is a clean implementation that can run alongside the existing viewer
 */
export function FloorPlanViewerV2(props: FloorPlanViewerV2Props) {
  const { hideHeader = false, viewMode = 'v2', onViewModeChange, ...viewerProps } = props;
  const { isReady, error } = useFloorPlanSetup(props.floorPlan);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Initialize project icon style
  useProjectIconStyle(props.floorPlan.id);

  // Get scale data
  const { scaleData, hasScale, refetch: refetchScale } = useFloorPlanScale(props.floorPlan.id);

  // Scale onboarding
  const isDwgFile = props.floorPlan.image_url?.toLowerCase().includes('.dwg') || false;

  // Clear undo/redo history when floor plan changes
  const clearHistoryForFloorPlan = useUndoRedoStore(state => state.clearHistoryForFloorPlan);
  useEffect(() => {
    // Clear history for this specific floor plan when component mounts
    // This ensures we don't have undo/redo actions from other floor plans
    return () => {
      // Optionally clear history when unmounting
      // clearHistoryForFloorPlan(props.floorPlan.id);
    };
  }, [props.floorPlan.id]);

  // Reset room opacity to 15% on floor plan load
  const setRoomOpacity = useAppStore(state => state.setRoomOpacity);
  useEffect(() => {
    setRoomOpacity(0.15);
  }, [props.floorPlan.id, setRoomOpacity]);

  // Tab state
  const [activeTab, setActiveTab] = useState<'floorPlan' | 'loads' | 'reports'>('floorPlan');

  // Local state for toolbar
  const [selectedTool, setSelectedTool] = useState<
    'select' | 'pan' | 'create-fixture' | 'create-strip' | 'create-panel' | 'create-room'
  >('select');
  const [isCircuitMode, setIsCircuitMode] = useState(false);
  const [selectedControlType, setSelectedControlType] = useState<
    'keypad' | 'dimmer' | 'switch' | 'auxiliary' | 'centralized-keypad' | null
  >(null);
  const [isControlPlacementMode, setIsControlPlacementMode] = useState(false);
  const [isRoomCreationMode, setIsRoomCreationMode] = useState(false);
  const [selectedProductCategory, setSelectedProductCategory] = useState<
    | 'camera'
    | 'speaker'
    | 'access-point'
    | 'touch-panel'
    | 'doorbell'
    | 'sensor'
    | 'group'
    | 'other'
    | null
  >(null);
  const [isProductPlacementMode, setIsProductPlacementMode] = useState(false);
  const [selectedFixtureCategory, setSelectedFixtureCategory] = useState<string | null>(null);
  const [selectedFixtureSubcategory, setSelectedFixtureSubcategory] = useState<string | null>(null);
  const [isFixturePlacementMode, setIsFixturePlacementMode] = useState(false);
  const [isCablePathMode, setIsCablePathMode] = useState(false);
  const [isCableJunctionMode, setIsCableJunctionMode] = useState(false);

  // Active users state (managed by RealtimeCollaborationHandler)
  const [activeUsers, setActiveUsers] = useState<any[]>([]);

  // Broadcast functions state (managed by RealtimeCollaborationHandler)
  const [broadcastFunctions, setBroadcastFunctions] = useState<{
    broadcastDrag: (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementId: string,
      x: number,
      y: number,
    ) => Promise<void>;
    broadcastDragEnd: (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementId: string,
    ) => Promise<void>;
    broadcastCursor: (x: number, y: number) => Promise<void>;
    broadcastCursorLeave: () => Promise<void>;
    broadcastSelection: (
      elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
      elementIds: string[],
    ) => Promise<void>;
  } | null>(null);

  // Remote drags state (managed by RealtimeCollaborationHandler)
  const [remoteDrags, setRemoteDrags] = useState<Map<string, RemoteDragState>>(new Map());

  // Remote cursors state (managed by RealtimeCollaborationHandler)
  const [remoteCursors, setRemoteCursors] = useState<Map<string, RemoteCursor>>(new Map());

  // Remote selections state (managed by RealtimeCollaborationHandler)
  const [remoteSelections, setRemoteSelections] = useState<Map<string, RemoteSelection>>(new Map());

  // Portal mapping update handler for realtime collaboration
  const handlePortalMappingUpdate = useCallback(
    (payload: any) => {
      // Broadcast portal mapping change to all components
      // This ensures ProposalItemsDrawerV2 refetches data
      logger.log('[FloorPlanViewer] Portal mapping updated, broadcasting event');
      window.dispatchEvent(
        new CustomEvent('portal-mapping-changed', {
          detail: {
            floorPlanId: props.floorPlan.id,
            payload,
          },
        }),
      );
    },
    [props.floorPlan.id],
  );

  // Cursor tracking for realtime collaboration
  const lastCursorBroadcastRef = useRef<number>(0);
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!broadcastFunctions) return;

      // Throttle cursor broadcasts to max 20x/sec (50ms intervals)
      const now = Date.now();
      const timeSinceLastBroadcast = now - lastCursorBroadcastRef.current;
      const CURSOR_BROADCAST_THROTTLE_MS = 50;

      if (timeSinceLastBroadcast >= CURSOR_BROADCAST_THROTTLE_MS) {
        lastCursorBroadcastRef.current = now;

        // Get current viewport-relative mouse position
        const rect = e.currentTarget.getBoundingClientRect();
        const viewportX = e.clientX - rect.left;
        const viewportY = e.clientY - rect.top;

        // Convert to floor plan coordinates (accounting for pan and zoom)
        // This ensures cursor shows in same floor plan location regardless of user's viewport state
        const viewport = useAppStore.getState().floorPlan.viewport;
        const floorPlanX = (viewportX - viewport.positionX) / viewport.scale;
        const floorPlanY = (viewportY - viewport.positionY) / viewport.scale;

        broadcastFunctions.broadcastCursor(floorPlanX, floorPlanY).catch(err => {
          logger.error('[Cursor] Failed to broadcast cursor:', err);
        });
      }
    },
    [broadcastFunctions],
  );

  const handleMouseLeave = useCallback(() => {
    if (!broadcastFunctions) return;
    broadcastFunctions.broadcastCursorLeave().catch(err => {
      logger.error('[Cursor] Failed to broadcast cursor leave:', err);
    });
  }, [broadcastFunctions]);

  // Measurement state
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementCallback, setMeasurementCallback] = useState<
    ((pixels: number) => void) | undefined
  >();

  // Circuit mode state
  const [circuitDrawerOpen, setCircuitDrawerOpen] = useState(false);
  const [newCircuitName, setNewCircuitName] = useState('');

  // Store actions
  const setActiveTool = useAppStore(state => state.setActiveTool);
  const openDrawer = useAppStore(state => state.setActiveDrawer);
  const clearAllSelections = useAppStore(state => state.clearAllSelections);
  const selectedFixtures = useAppStore(state => state.selection.fixtures);
  const selectedControls = useAppStore(state => state.selection.controls);
  const selectedProducts = useAppStore(state => state.selection.products);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const setActiveMode = useAppStore(state => state.setActiveMode);
  const selectedRooms = useSelectedRooms();
  const selectedAnnotations = useSelectedAnnotations();
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);

  // Broadcast selection changes to other users
  useEffect(() => {
    if (!broadcastFunctions) return;

    // Determine which selection type has items and broadcast accordingly
    // We prioritize in this order: fixtures, controls, products, annotations, rooms, panels
    let elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel' | null =
      null;
    let elementIds: string[] = [];

    if (selectedFixtures && selectedFixtures.size > 0) {
      elementType = 'fixture';
      elementIds = Array.from(selectedFixtures);
    } else if (selectedControls && selectedControls.size > 0) {
      elementType = 'control';
      elementIds = Array.from(selectedControls);
    } else if (selectedProducts && selectedProducts.size > 0) {
      elementType = 'product';
      elementIds = Array.from(selectedProducts);
    } else if (selectedAnnotations && selectedAnnotations.size > 0) {
      elementType = 'annotation';
      elementIds = Array.from(selectedAnnotations);
    } else if (selectedRooms && selectedRooms.size > 0) {
      elementType = 'room';
      elementIds = Array.from(selectedRooms);
    }

    // Broadcast the selection (or clear if nothing selected)
    if (elementType) {
      broadcastFunctions.broadcastSelection(elementType, elementIds).catch(err => {
        logger.error('[Selection] Failed to broadcast selection:', err);
      });
    } else {
      // Clear remote selections by broadcasting empty array
      broadcastFunctions.broadcastSelection('fixture', []).catch(err => {
        logger.error('[Selection] Failed to clear selection broadcast:', err);
      });
    }
  }, [
    selectedFixtures,
    selectedControls,
    selectedProducts,
    selectedAnnotations,
    selectedRooms,
    broadcastFunctions,
  ]);

  // Elevation mode state
  const isWallSelectionMode = useAppStore(state => state.elevations.isWallSelectionMode);
  const setWallSelectionMode = useAppStore(state => state.setWallSelectionMode);

  // Exit circuit mode - defined early to avoid hoisting issues
  const exitCircuitMode = useCallback(() => {
    setIsCircuitMode(false);
    setCircuitDrawerOpen(false);
    setActiveDrawer(null); // Clear the drawer in the app store
    clearAllSelections();
    setNewCircuitName('');
    setActiveMode('default');
  }, [clearAllSelections, setActiveMode, setActiveDrawer]);

  // Unified Placement Palette state - must be before handleToolSelect
  const {
    isPaletteOpen,
    openPalette,
    closePalette,
    currentItem: paletteCurrentItem,
    placementCount: palettePlacementCount,
    selectItem: selectPaletteItem,
    clearPlacement: clearPalettePlacement,
    recordPlacement,
    recentItems,
  } = useUnifiedPlacement();

  // AI Product Creation Modal state
  const [aiProductModalOpen, setAiProductModalOpen] = useState(false);
  const [aiProductInitialQuery, setAiProductInitialQuery] = useState('');

  // Handle AI product creation from palette
  const handleCreateWithAI = useCallback((query: string) => {
    setAiProductInitialQuery(query);
    setAiProductModalOpen(true);
  }, []);

  // Handle tool selection
  const handleToolSelect = useCallback(
    (
      tool: 'select' | 'pan' | 'create-fixture' | 'create-strip' | 'create-panel' | 'create-room',
    ) => {
      setSelectedTool(tool);

      // Clear unified palette placement mode when switching tools
      clearPalettePlacement();

      // Exit all modes when selecting any tool
      if (isCircuitMode) {
        exitCircuitMode();
      }

      // Exit elevation mode when selecting any tool
      if (isWallSelectionMode) {
        setWallSelectionMode(false);
      }

      // Exit cable path mode when selecting any tool
      if (isCablePathMode) {
        setIsCablePathMode(false);
        const cancelCablePathDrawing = useAppStore.getState().cancelCablePathDrawing;
        cancelCablePathDrawing();
      }

      // Exit cable junction placement mode when selecting any tool
      if (isCableJunctionMode) {
        setIsCableJunctionMode(false);
      }

      // Always clear control placement mode when switching tools
      setIsControlPlacementMode(false);
      setSelectedControlType(null);

      // Always disable annotation mode when selecting any tool
      const setActiveAnnotationType = useAppStore.getState().setActiveAnnotationType;
      setActiveAnnotationType(null);

      // Always clear product placement mode when switching tools
      setIsProductPlacementMode(false);
      setSelectedProductCategory(null);

      // Clear fixture placement mode when switching tools (unless we're selecting the fixture tool)
      if (tool !== 'create-fixture') {
        setIsFixturePlacementMode(false);
        setSelectedFixtureCategory(null);
        setSelectedFixtureSubcategory(null);
      }

      // Clear measurement mode when switching tools
      setIsMeasuring(false);

      // Clear room creation mode unless we're selecting the room tool
      if (tool !== 'create-room') {
        setIsRoomCreationMode(false);
      }

      // Handle room visibility based on tool
      const setRoomVisibility = useAppStore.getState().setRoomVisibility;
      if (tool === 'create-fixture') {
        // Hide rooms when placing fixtures for better visibility
        setRoomVisibility(false);
      } else if (tool === 'create-room') {
        // Show rooms when creating rooms
        setRoomVisibility(true);
      }

      // Update store based on tool
      if (tool === 'select') {
        setActiveTool('select');
        setActiveMode('default');
      } else if (tool === 'pan') {
        setActiveTool('pan');
        setActiveMode('default');
      } else if (tool === 'create-strip') {
        // Keep strip drawing in the local toolbar state; global tool remains a valid draw tool.
        setActiveTool('draw');
        setActiveMode('default');
      } else if (tool === 'create-fixture' || tool === 'create-panel' || tool === 'create-room') {
        setActiveTool('draw');
        setActiveMode('default');
      }
    },
    [
      setActiveTool,
      isCircuitMode,
      exitCircuitMode,
      setActiveMode,
      isWallSelectionMode,
      setWallSelectionMode,
      clearPalettePlacement,
      isCablePathMode,
      isCableJunctionMode,
    ],
  );

  // Handle circuit mode toggle
  const handleCircuitModeToggle = useCallback(() => {
    const newMode = !isCircuitMode;
    setIsCircuitMode(newMode);

    if (newMode) {
      // Enter circuit mode - deactivate all other modes
      setIsControlPlacementMode(false);
      setSelectedControlType(null);
      setIsProductPlacementMode(false);
      setSelectedProductCategory(null);
      setIsRoomCreationMode(false);
      setIsFixturePlacementMode(false);
      setSelectedFixtureCategory(null);
      setSelectedFixtureSubcategory(null);
      // Clear elevation mode
      if (isWallSelectionMode) {
        setWallSelectionMode(false);
      }
      // Clear measurement mode
      if (isMeasuring) {
        setIsMeasuring(false);
      }
      // Clear cable path mode
      if (isCablePathMode) {
        setIsCablePathMode(false);
        const cancelCablePathDrawing = useAppStore.getState().cancelCablePathDrawing;
        cancelCablePathDrawing();
      }
      // Clear cable junction placement mode
      if (isCableJunctionMode) {
        setIsCableJunctionMode(false);
      }
      // Clear annotation mode - using the app store
      const annotationState = useAppStore.getState().annotations;
      if (annotationState.activeType) {
        useAppStore.getState().setActiveAnnotationType(null);
      }

      // Setup circuit mode
      clearAllSelections();
      setCircuitDrawerOpen(true);
      setActiveDrawer('circuit'); // Set the drawer in the app store
      setNewCircuitName('');
      setSelectedTool('select');
      setActiveTool('select');
      setActiveMode('circuit');
    } else {
      // Exit circuit mode
      setCircuitDrawerOpen(false);
      setActiveDrawer(null); // Clear the drawer in the app store
      clearAllSelections();
      setNewCircuitName('');
      setActiveTool('select');
      setActiveMode('default');
    }
  }, [
    isCircuitMode,
    clearAllSelections,
    setActiveMode,
    setActiveTool,
    setActiveDrawer,
    isWallSelectionMode,
    isMeasuring,
    isCablePathMode,
    isCableJunctionMode,
  ]);

  // Track if rooms were hidden when entering control placement mode
  const [roomsHiddenByControlMode, setRoomsHiddenByControlMode] = useState(false);

  // Handle control type selection
  const handleControlTypeSelect = useCallback(
    (type: 'keypad' | 'dimmer' | 'switch' | 'auxiliary' | 'centralized-keypad' | null) => {
      logger.log(
        '[Control] handleControlTypeSelect called with:',
        type,
        'current mode:',
        isControlPlacementMode,
        'current type:',
        selectedControlType,
      );

      // Override any pending cmd+K palette item (see handleFixtureCategorySelect).
      if (type !== null) {
        clearPalettePlacement();
      }

      // Exit circuit mode when placing controls
      if (isCircuitMode && type !== null) {
        exitCircuitMode();
      }

      // Exit room creation mode
      if (type !== null) {
        setIsRoomCreationMode(false);
      }

      // Clear measurement mode when placing controls
      if (isMeasuring && type !== null) {
        setIsMeasuring(false);
      }

      // Clear fixture placement mode when placing controls
      if (isFixturePlacementMode && type !== null) {
        setIsFixturePlacementMode(false);
        setSelectedFixtureCategory(null);
        setSelectedFixtureSubcategory(null);
      }

      // Check if we're just switching between control types (not entering/exiting mode)
      const wasAlreadyInControlPlacement = isControlPlacementMode;
      const isJustSwitchingTypes = wasAlreadyInControlPlacement && type !== null;

      setSelectedControlType(type);
      setIsControlPlacementMode(type !== null);

      // Handle room visibility
      const setRoomVisibility = useAppStore.getState().setRoomVisibility;
      const getCurrentRoomVisibility = () => useAppStore.getState().rooms.roomsVisible;

      if (type !== null) {
        // Entering or staying in control placement mode
        if (!wasAlreadyInControlPlacement) {
          // First time entering control placement mode
          const currentVisibility = getCurrentRoomVisibility();
          if (currentVisibility) {
            // Only hide rooms if they're currently visible
            logger.log('[Control] Hiding rooms - entering control placement mode for first time');
            setRoomVisibility(false);
            setRoomsHiddenByControlMode(true);
          }
        } else {
          // Just switching between control types - don't change anything
          logger.log('[Control] Switching control types - NOT changing room visibility');
        }

        setSelectedTool('select');
        setActiveTool('select');
        setActiveMode('control-placement');
        clearAllSelections();
      } else {
        // Exiting control placement mode
        if (wasAlreadyInControlPlacement && roomsHiddenByControlMode) {
          // Only restore room visibility if we hid them when entering control mode
          logger.log('[Control] Restoring room visibility - exiting control placement mode');
          setRoomVisibility(true);
          setRoomsHiddenByControlMode(false);
        }
        setActiveMode('default');
      }
    },
    [
      clearAllSelections,
      isCircuitMode,
      exitCircuitMode,
      setActiveTool,
      setActiveMode,
      isControlPlacementMode,
      selectedControlType,
      roomsHiddenByControlMode,
      isMeasuring,
    ],
  );

  // Handle control placement toggle
  const handleControlPlacementToggle = useCallback(() => {
    if (isControlPlacementMode) {
      // Exit control placement mode
      handleControlTypeSelect(null);
    } else {
      // Enter control placement mode with the last selected type
      if (selectedControlType) {
        handleControlTypeSelect(selectedControlType);
      }
    }
  }, [isControlPlacementMode, selectedControlType, handleControlTypeSelect]);

  // Handle room creation toggle
  const handleRoomCreationToggle = useCallback(() => {
    // Exit circuit mode when toggling room creation
    if (isCircuitMode) {
      exitCircuitMode();
    }
    setIsRoomCreationMode(!isRoomCreationMode);
  }, [isRoomCreationMode, isCircuitMode, exitCircuitMode]);

  // Handle product category selection with subcategory support
  const handleProductCategorySelect = useCallback(
    (category: string | null, subcategory?: string | null) => {
      logger.log('[Product] handleProductCategorySelect called with:', { category, subcategory });

      // Override any pending cmd+K palette item (see handleFixtureCategorySelect).
      if (category !== null) {
        clearPalettePlacement();
      }

      // Exit other modes when placing products
      if (isCircuitMode && category !== null) {
        exitCircuitMode();
      }

      if (isControlPlacementMode && category !== null) {
        setIsControlPlacementMode(false);
        setSelectedControlType(null);
      }

      if (isRoomCreationMode && category !== null) {
        setIsRoomCreationMode(false);
      }

      if (isWallSelectionMode && category !== null) {
        setWallSelectionMode(false);
      }

      // Clear measurement mode when placing products
      if (isMeasuring && category !== null) {
        setIsMeasuring(false);
      }

      // Format category with subcategory if provided
      const formattedCategory = subcategory ? `${category}:${subcategory}` : category;
      setSelectedProductCategory(formattedCategory as any);
      setIsProductPlacementMode(category !== null);

      if (category !== null) {
        setSelectedTool('select');
        setActiveTool('select');
        setActiveMode('product-placement');
        clearAllSelections();
        logger.log('[Product] Product placement mode activated for:', formattedCategory);
      } else {
        setActiveMode('default');
        logger.log('[Product] Product placement mode deactivated');
      }
    },
    [
      clearAllSelections,
      isCircuitMode,
      exitCircuitMode,
      setActiveTool,
      setActiveMode,
      isControlPlacementMode,
      isRoomCreationMode,
      isWallSelectionMode,
      setWallSelectionMode,
      isMeasuring,
    ],
  );

  // Handle fixture category selection
  const handleFixtureCategorySelect = useCallback(
    (category: string | null, subcategory?: string | null) => {
      logger.log('[Fixture] Category selection:', category, subcategory);

      // Exit other modes when selecting fixture category
      if (isCircuitMode && category !== null) {
        exitCircuitMode();
      }

      if (isControlPlacementMode && category !== null) {
        setIsControlPlacementMode(false);
        setSelectedControlType(null);
      }

      if (isProductPlacementMode && category !== null) {
        setIsProductPlacementMode(false);
        setSelectedProductCategory(null);
      }

      if (isRoomCreationMode && category !== null) {
        setIsRoomCreationMode(false);
      }

      if (isWallSelectionMode && category !== null) {
        setWallSelectionMode(false);
      }

      // Clear measurement mode when selecting fixtures
      if (isMeasuring && category !== null) {
        setIsMeasuring(false);
      }

      setSelectedFixtureCategory(category);
      setSelectedFixtureSubcategory(subcategory || null);

      // Only set fixture placement mode when both category and subcategory are selected
      // or when selecting fixture marker (both null)
      const isPlacementMode =
        (category !== null && subcategory !== null) || (category === null && subcategory === null);
      setIsFixturePlacementMode(isPlacementMode);

      if (isPlacementMode) {
        // A toolbar fixture selection (marker OR specific category) must override
        // any pending cmd+K palette item: FloorPlanViewport prioritizes the
        // palette's placementItem in the click handler, so a stale selection would
        // keep getting placed after switching tools. The fixture marker is called
        // as (null, null) so a `category !== null` guard would miss it — clearing
        // here (inside the placement branch) covers both. The palette path re-sets
        // its item right after this (handlePaletteSelect → selectPaletteItem), so
        // this is safe there too.
        clearPalettePlacement();
        setSelectedTool('create-fixture');
        setActiveTool('draw');
        setActiveMode('default');
        logger.log(
          '[Fixture] Placement mode activated for:',
          category || 'marker',
          subcategory || '',
        );
      } else if (category === null) {
        // Clearing fixture category completely
        setSelectedTool('select');
        setActiveTool('select');
        logger.log('[Fixture] Placement mode deactivated');
      }
    },
    [
      isCircuitMode,
      exitCircuitMode,
      isControlPlacementMode,
      isProductPlacementMode,
      isRoomCreationMode,
      isWallSelectionMode,
      setWallSelectionMode,
      setActiveTool,
      setActiveMode,
      isMeasuring,
    ],
  );

  // Handle elevation mode toggle
  const handleElevationModeToggle = useCallback(() => {
    const newMode = !isWallSelectionMode;
    setWallSelectionMode(newMode);

    if (newMode) {
      // Enter elevation mode - deactivate all other modes
      if (isCircuitMode) {
        exitCircuitMode();
      }
      setIsControlPlacementMode(false);
      setSelectedControlType(null);
      setIsProductPlacementMode(false);
      setSelectedProductCategory(null);
      setIsRoomCreationMode(false);
      setIsFixturePlacementMode(false);
      setSelectedFixtureCategory(null);
      setSelectedFixtureSubcategory(null);
      setIsCablePathMode(false);

      // Setup elevation mode
      setSelectedTool('select');
      setActiveTool('select');
      clearAllSelections();

      // In elevation mode, we don't show room fills
      // The walls will be shown by the WallSelectionMode component
      const setRoomVisibility = useAppStore.getState().setRoomVisibility;
      setRoomVisibility(false);
    } else {
      // Exit elevation mode
      setActiveTool('select');
      // Optionally restore room visibility if desired
      // const setRoomVisibility = useAppStore.getState().setRoomVisibility;
      // setRoomVisibility(false);
    }
  }, [
    isWallSelectionMode,
    setWallSelectionMode,
    isCircuitMode,
    exitCircuitMode,
    setActiveTool,
    clearAllSelections,
  ]);

  // Handle cable path mode toggle
  const handleCablePathModeToggle = useCallback(() => {
    const newMode = !isCablePathMode;
    setIsCablePathMode(newMode);

    if (newMode) {
      // Enter cable path mode - deactivate all other modes
      if (isCircuitMode) {
        exitCircuitMode();
      }
      setIsControlPlacementMode(false);
      setSelectedControlType(null);
      setIsProductPlacementMode(false);
      setSelectedProductCategory(null);
      setIsRoomCreationMode(false);
      setIsFixturePlacementMode(false);
      setSelectedFixtureCategory(null);
      setSelectedFixtureSubcategory(null);
      if (isWallSelectionMode) {
        setWallSelectionMode(false);
      }
      if (isMeasuring) {
        setIsMeasuring(false);
      }

      // Setup cable path mode
      setSelectedTool('select');
      setActiveTool('select');
      setActiveMode('cable-path-drawing');
      clearAllSelections();
    } else {
      // Exit cable path mode
      setActiveTool('select');
      setActiveMode('default');
      // Cancel any active drawing state
      const cancelCablePathDrawing = useAppStore.getState().cancelCablePathDrawing;
      cancelCablePathDrawing();
    }
  }, [
    isCablePathMode,
    isCircuitMode,
    exitCircuitMode,
    setActiveTool,
    setActiveMode,
    clearAllSelections,
    isWallSelectionMode,
    setWallSelectionMode,
    isMeasuring,
  ]);

  // Handle cable junction placement mode toggle
  const handleCableJunctionModeToggle = useCallback(() => {
    const newMode = !isCableJunctionMode;
    setIsCableJunctionMode(newMode);

    if (newMode) {
      // Enter cable junction placement mode - deactivate all other modes
      if (isCircuitMode) {
        exitCircuitMode();
      }
      if (isCablePathMode) {
        setIsCablePathMode(false);
        setActiveMode('default');
        const cancelCablePathDrawing = useAppStore.getState().cancelCablePathDrawing;
        cancelCablePathDrawing();
      }
      setIsControlPlacementMode(false);
      setSelectedControlType(null);
      setIsProductPlacementMode(false);
      setSelectedProductCategory(null);
      setIsRoomCreationMode(false);
      setIsFixturePlacementMode(false);
      setSelectedFixtureCategory(null);
      setSelectedFixtureSubcategory(null);
      if (isWallSelectionMode) {
        setWallSelectionMode(false);
      }
      if (isMeasuring) {
        setIsMeasuring(false);
      }

      // Setup cable junction placement mode
      setSelectedTool('select');
      setActiveTool('select');
      setActiveMode('cable-junction-placement');
      clearAllSelections();
    } else {
      // Exit cable junction placement mode
      setActiveTool('select');
      setActiveMode('default');
    }
  }, [
    isCableJunctionMode,
    isCablePathMode,
    isCircuitMode,
    exitCircuitMode,
    setActiveTool,
    setActiveMode,
    clearAllSelections,
    isWallSelectionMode,
    setWallSelectionMode,
    isMeasuring,
  ]);

  // Handle palette item selection - wire to existing placement handlers
  const handlePaletteSelect = useCallback(
    (item: PlacementItem) => {
      logger.log('[Palette] Item selected:', item);

      // Close the palette
      closePalette();

      // Wire to the appropriate existing handler based on item type
      switch (item.type) {
        case 'fixture':
          handleFixtureCategorySelect(item.category, item.subcategory);
          break;
        case 'control': {
          // Map category to control type
          const controlTypeMap: Record<
            string,
            'keypad' | 'dimmer' | 'switch' | 'auxiliary' | 'centralized-keypad'
          > = {
            keypad: 'keypad',
            dimmer: 'dimmer',
            switch: 'switch',
            auxiliary: 'auxiliary',
            'centralized-keypad': 'centralized-keypad',
          };
          const controlType = controlTypeMap[item.category] || 'keypad';
          handleControlTypeSelect(controlType);
          break;
        }
        case 'product':
          handleProductCategorySelect(item.category, item.subcategory);
          break;
      }

      // Record the selection for recents
      selectPaletteItem(item);
    },
    [
      closePalette,
      handleFixtureCategorySelect,
      handleControlTypeSelect,
      handleProductCategorySelect,
      selectPaletteItem,
    ],
  );

  // Handle clearing palette placement
  const handleClearPalettePlacement = useCallback(() => {
    clearPalettePlacement();
    // Also clear the current placement mode
    if (isFixturePlacementMode) {
      handleFixtureCategorySelect(null);
    }
    if (isControlPlacementMode) {
      handleControlTypeSelect(null);
    }
    if (isProductPlacementMode) {
      handleProductCategorySelect(null);
    }
  }, [
    clearPalettePlacement,
    isFixturePlacementMode,
    isControlPlacementMode,
    isProductPlacementMode,
    handleFixtureCategorySelect,
    handleControlTypeSelect,
    handleProductCategorySelect,
  ]);

  // Handle settings - open the settings drawer
  const handleSettingsClick = useCallback(() => {
    openDrawer('settings');
  }, [openDrawer]);

  const handleSettingsOpen = useCallback(() => {
    openDrawer('settings');
  }, [openDrawer]);

  // Handle measurement toggle
  const handleMeasurementToggle = useCallback(() => {
    const newMeasuringState = !isMeasuring;

    if (newMeasuringState) {
      // Clear all other modes when entering measurement mode
      if (isCircuitMode) {
        exitCircuitMode();
      }
      if (isControlPlacementMode) {
        setIsControlPlacementMode(false);
        setSelectedControlType(null);
      }
      if (isProductPlacementMode) {
        setIsProductPlacementMode(false);
        setSelectedProductCategory(null);
      }
      if (isFixturePlacementMode) {
        setIsFixturePlacementMode(false);
        setSelectedFixtureCategory(null);
        setSelectedFixtureSubcategory(null);
      }
      if (isRoomCreationMode) {
        setIsRoomCreationMode(false);
      }
      if (isWallSelectionMode) {
        setWallSelectionMode(false);
      }
    }

    setIsMeasuring(newMeasuringState);
  }, [
    isMeasuring,
    isCircuitMode,
    exitCircuitMode,
    isControlPlacementMode,
    isProductPlacementMode,
    isFixturePlacementMode,
    isRoomCreationMode,
    isWallSelectionMode,
    setWallSelectionMode,
  ]);

  // Handle scale tool click - open settings drawer on scale tab or toggle measurement
  const handleScaleToolClick = useCallback(() => {
    if (hasScale) {
      // If scale is set, toggle measurement mode
      handleMeasurementToggle();
    } else {
      // If no scale, open settings drawer on scale tab
      const setSettingsTab = useAppStore.getState().setSettingsTab;
      setSettingsTab('scale');
      setActiveDrawer('settings');
    }
  }, [setActiveDrawer, hasScale, handleMeasurementToggle]);

  // Handle measurement start with callback
  const handleStartMeasurement = useCallback((callback: (pixels: number) => void) => {
    setIsMeasuring(true);
    setMeasurementCallback(() => callback);
  }, []);

  // Handle measurement stop
  const handleStopMeasurement = useCallback(() => {
    setIsMeasuring(false);
    setMeasurementCallback(undefined);
  }, []);

  // Use scale onboarding
  useScaleOnboarding({
    floorPlanId: props.floorPlan.id,
    onScaleToolClick: handleScaleToolClick,
    isDwgFile,
  });

  // Sync local tool state when floor plan image editing is enabled
  const isEditingFloorPlanImage = useAppStore(state => state.ui.isEditingFloorPlanImage);
  useEffect(() => {
    if (isEditingFloorPlanImage) {
      // Reset local tool state to select
      setSelectedTool('select');
    }
  }, [isEditingFloorPlanImage]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle Cmd+K / Ctrl+K to open command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isPaletteOpen) {
          closePalette();
        } else {
          openPalette('all');
        }
        return;
      }

      // Handle ESC key to exit modes
      if (e.key === 'Escape') {
        // Close palette if open
        if (isPaletteOpen) {
          closePalette();
          return;
        }

        // Clear palette placement if active
        if (paletteCurrentItem) {
          handleClearPalettePlacement();
          return;
        }
        // Exit measurement mode if active
        if (isMeasuring) {
          setIsMeasuring(false);
          setMeasurementCallback(undefined);
          return; // Don't do anything else if we're just exiting measurement
        }

        // Exit all modes and return to select tool
        if (isCircuitMode) {
          exitCircuitMode();
        }
        if (isControlPlacementMode) {
          setIsControlPlacementMode(false);
          setSelectedControlType(null);
          // Restore room visibility only if we hid them when entering control mode
          if (roomsHiddenByControlMode) {
            const setRoomVisibility = useAppStore.getState().setRoomVisibility;
            setRoomVisibility(true);
            setRoomsHiddenByControlMode(false);
          }
        }
        if (isRoomCreationMode) {
          setIsRoomCreationMode(false);
        }
        if (isProductPlacementMode) {
          setIsProductPlacementMode(false);
          setSelectedProductCategory(null);
        }
        if (isWallSelectionMode) {
          setWallSelectionMode(false);
        }
        // Exit cable path mode
        if (isCablePathMode) {
          setIsCablePathMode(false);
          const cancelCablePathDrawing = useAppStore.getState().cancelCablePathDrawing;
          cancelCablePathDrawing();
        }
        // Exit cable junction placement mode
        if (isCableJunctionMode) {
          setIsCableJunctionMode(false);
        }
        // Exit fixture placement mode
        if (isFixturePlacementMode || selectedTool === 'create-fixture') {
          setIsFixturePlacementMode(false);
          setSelectedFixtureCategory(null);
          setSelectedFixtureSubcategory(null);
        }

        // Check if hand tool is active
        const currentActiveTool = useAppStore.getState().ui.toolbar.activeTool;
        if (currentActiveTool === 'pan' || selectedTool === 'pan') {
          // Exit hand tool
          setSelectedTool('select');
        }

        // Return to select tool
        setSelectedTool('select');
        setActiveTool('select');
        setActiveMode('default');
      }

      // Handle M key for measurement mode toggle
      if (e.key === 'm' || e.key === 'M') {
        // Don't toggle if user is typing in an input
        const activeElement = document.activeElement;
        const isTyping =
          activeElement &&
          (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.getAttribute('contenteditable') === 'true');

        if (!isTyping) {
          e.preventDefault();
          handleMeasurementToggle();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isCircuitMode,
    isControlPlacementMode,
    isRoomCreationMode,
    isProductPlacementMode,
    isWallSelectionMode,
    isFixturePlacementMode,
    isCablePathMode,
    isCableJunctionMode,
    selectedTool,
    exitCircuitMode,
    setActiveTool,
    setActiveMode,
    roomsHiddenByControlMode,
    isMeasuring,
    handleMeasurementToggle,
    setWallSelectionMode,
    isPaletteOpen,
    openPalette,
    closePalette,
    paletteCurrentItem,
    handleClearPalettePlacement,
  ]);

  // Handle room selection
  useEffect(() => {
    if (selectedRooms.size > 0 && !isCircuitMode && !isControlPlacementMode) {
      setActiveDrawer('room');
    }
  }, [selectedRooms.size, isCircuitMode, isControlPlacementMode, setActiveDrawer]);

  // Auto-disable placement tools when element drawers open
  // This syncs the parent component's placement state with the store's drawer state
  useEffect(() => {
    const ELEMENT_DRAWERS = [
      'fixture',
      'control',
      'product',
      'room',
      'circuit',
      'panel',
      'selection',
    ];

    if (activeDrawer && ELEMENT_DRAWERS.includes(activeDrawer)) {
      // Reset placement modes when clicking on elements
      if (isControlPlacementMode) {
        setIsControlPlacementMode(false);
        setSelectedControlType(null);
      }
      if (isProductPlacementMode) {
        setIsProductPlacementMode(false);
        setSelectedProductCategory(null);
      }
      if (isFixturePlacementMode || selectedTool === 'create-fixture') {
        setIsFixturePlacementMode(false);
        setSelectedFixtureCategory(null);
        setSelectedFixtureSubcategory(null);
        setSelectedTool('select'); // Reset the selected tool
      }
    }
  }, [
    activeDrawer,
    isControlPlacementMode,
    isProductPlacementMode,
    isFixturePlacementMode,
    selectedTool,
  ]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 mb-2">Error loading floor plan</p>
          <p className="text-sm text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin h-8 w-8" />
      </div>
    );
  }

  return (
    <FloorPlanErrorBoundary>
      <FloorPlanProvider floorPlan={props.floorPlan} broadcastFunctions={broadcastFunctions}>
        {/* Realtime collaboration handler - manages real-time updates from other users */}
        <RealtimeCollaborationHandler
          floorPlanId={props.floorPlan.id}
          isPublicView={props.isPublicView}
          onActiveUsersChange={setActiveUsers}
          onPortalMappingUpdate={handlePortalMappingUpdate}
          onBroadcastFunctionsReady={setBroadcastFunctions}
          onRemoteDragsChange={setRemoteDrags}
          onRemoteCursorsChange={setRemoteCursors}
          onRemoteSelectionsChange={setRemoteSelections}
        />

        <div className="floor-plan-viewer-v2 h-full flex flex-col bg-gray-50">
          {!hideHeader && (
            <FloorPlanHeader
              floorPlan={props.floorPlan}
              onBack={props.onBack}
              isPublicView={props.isPublicView}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              activeUsers={activeUsers}
            />
          )}

          {!props.isPublicView && (
            <FloorPlanToolbar activeTab={activeTab} onTabChange={setActiveTab} />
          )}

          <div
            className={`flex-1 relative overflow-hidden ${!props.isPublicView && activeTab === 'floorPlan' ? 'pl-[72px]' : ''}`}
            onMouseMove={!props.isPublicView ? handleMouseMove : undefined}
            onMouseLeave={!props.isPublicView ? handleMouseLeave : undefined}
          >
            {activeTab === 'floorPlan' && (
              <>
                <FloorPlanViewport
                  floorPlan={props.floorPlan}
                  onProcessComplete={() => {
                    // If we were in room creation mode, return to select tool
                    if (selectedTool === 'create-room') {
                      setSelectedTool('select');
                      setActiveTool('select');
                    }
                    // Call the original onProcessComplete if provided
                    if (props.onProcessComplete) {
                      props.onProcessComplete();
                    }
                  }}
                  onToolSelect={handleToolSelect}
                  selectedTool={props.isPublicView ? 'pan' : selectedTool}
                  selectedControlType={selectedControlType}
                  isControlPlacementMode={props.isPublicView ? false : isControlPlacementMode}
                  selectedProductCategory={selectedProductCategory}
                  isProductPlacementMode={props.isPublicView ? false : isProductPlacementMode}
                  onProductCategorySelect={handleProductCategorySelect}
                  selectedFixtureCategory={selectedFixtureCategory}
                  selectedFixtureSubcategory={selectedFixtureSubcategory}
                  isCircuitMode={props.isPublicView ? false : isCircuitMode}
                  isPublicView={props.isPublicView}
                  isMeasuring={isMeasuring}
                  onMeasurementComplete={measurementCallback}
                  setIsMeasuring={setIsMeasuring}
                  isElevationMode={props.isPublicView ? false : isWallSelectionMode}
                  placementItem={props.isPublicView ? null : paletteCurrentItem}
                />

                {/* Remote drag indicators - show other users' live drag positions */}
                <RemoteDragIndicator remoteDrags={remoteDrags} />

                {/* Remote cursor indicators - show other users' cursor positions */}
                <RemoteCursorIndicator remoteCursors={remoteCursors} />

                {/* Remote selection indicators - show other users' selected elements */}
                <RemoteSelectionIndicator remoteSelections={remoteSelections} />

                {/* Left Toolbar - only show in edit mode and floor plan tab */}
                {!props.isPublicView && (
                  <LeftToolbarWithContext
                    onToolSelect={handleToolSelect}
                    selectedTool={selectedTool}
                    onCircuitModeToggle={handleCircuitModeToggle}
                    isCircuitMode={isCircuitMode}
                    onControlTypeSelect={handleControlTypeSelect}
                    selectedControlType={selectedControlType}
                    isControlPlacementMode={isControlPlacementMode}
                    onRoomCreationToggle={handleRoomCreationToggle}
                    isRoomCreationMode={isRoomCreationMode}
                    onProductCategorySelect={handleProductCategorySelect}
                    onElevationModeToggle={handleElevationModeToggle}
                    isElevationMode={isWallSelectionMode}
                    hasScale={hasScale}
                    selectedProductCategory={selectedProductCategory}
                    isProductPlacementMode={isProductPlacementMode}
                    onFixtureCategorySelect={handleFixtureCategorySelect}
                    selectedFixtureCategory={selectedFixtureCategory}
                    selectedFixtureSubcategory={selectedFixtureSubcategory}
                    isFixturePlacementMode={isFixturePlacementMode}
                    onScaleToolClick={handleScaleToolClick}
                    isScaleModeActive={false}
                    onMeasurementToggle={handleMeasurementToggle}
                    isMeasuring={isMeasuring}
                    onPaletteOpen={() => openPalette('all')}
                    onCablePathModeToggle={handleCablePathModeToggle}
                    isCablePathMode={isCablePathMode}
                    onCableJunctionModeToggle={handleCableJunctionModeToggle}
                    isCableJunctionMode={isCableJunctionMode}
                  />
                )}

                {/* Pending operations indicator */}
                {!props.isPublicView &&
                  featureFlags.isOptimisticCircuitCreationEnabled(isMobile) && (
                    <PendingOperationsIndicator floorPlanId={props.floorPlan.id} />
                  )}

                {/* Annotation contextual toolbar - only show if drawer is not open or if it's annotation-only selection */}
                {!props.isPublicView &&
                  selectedAnnotations.size > 0 &&
                  (() => {
                    const totalSelections =
                      selectedFixtures.size +
                      selectedControls.size +
                      selectedProducts.size +
                      selectedAnnotations.size +
                      selectedRooms.size;
                    const hasOnlyAnnotations = selectedAnnotations.size === totalSelections;

                    // Show toolbar only if:
                    // 1. Only annotations are selected (no drawer shown)
                    // 2. OR if the drawer is not the multi-selection drawer
                    return hasOnlyAnnotations || activeDrawer !== 'selection';
                  })() && (
                    <AnnotationToolbarWithContext
                      floorPlanId={props.floorPlan.id}
                      selectedAnnotations={selectedAnnotations}
                    />
                  )}
              </>
            )}
            {activeTab === 'loads' && <LoadsViewWithContext onTabChange={setActiveTab} />}
            {activeTab === 'reports' && props.floorPlan.project_id && (
              <div className="h-full overflow-auto bg-gray-50">
                <ProjectReports
                  projectId={props.floorPlan.project_id}
                  projectName={props.floorPlan.name}
                  currentFloorPlanId={props.floorPlan.id}
                  currentFloorPlanName={props.floorPlan.name}
                  defaultFloorPlanId={props.floorPlan.id}
                  compactHeader
                />
              </div>
            )}
          </div>

          {!props.isPublicView && activeTab === 'floorPlan' && (
            <FloorPlanDrawers
              onStartMeasurement={handleStartMeasurement}
              onStopMeasurement={handleStopMeasurement}
              isMeasuring={isMeasuring}
              onScaleUpdate={refetchScale}
            />
          )}

          {/* Circuit Creation Handler - only show in edit mode and floor plan tab */}
          {!props.isPublicView &&
            activeTab === 'floorPlan' &&
            (featureFlags.isOptimisticCircuitCreationEnabled(isMobile) ? (
              <CircuitCreationHandlerOptimistic
                circuitDrawerOpen={circuitDrawerOpen}
                setCircuitDrawerOpen={setCircuitDrawerOpen}
                exitCircuitMode={exitCircuitMode}
                isMobile={isMobile}
                newCircuitName={newCircuitName}
                setNewCircuitName={setNewCircuitName}
                floorPlanId={props.floorPlan.id}
                useOptimistic={true}
              />
            ) : (
              <CircuitCreationHandler
                circuitDrawerOpen={circuitDrawerOpen}
                setCircuitDrawerOpen={setCircuitDrawerOpen}
                exitCircuitMode={exitCircuitMode}
                isMobile={isMobile}
                newCircuitName={newCircuitName}
                setNewCircuitName={setNewCircuitName}
                floorPlanId={props.floorPlan.id}
              />
            ))}

          {/* Unified Placement Palette - Command palette for adding fixtures/controls/products */}
          {!props.isPublicView && (
            <UnifiedPlacementPalette
              open={isPaletteOpen}
              onOpenChange={open => {
                if (open) {
                  openPalette('all');
                } else {
                  closePalette();
                }
              }}
              onSelect={handlePaletteSelect}
              recentItems={recentItems}
              onCreateWithAI={handleCreateWithAI}
            />
          )}

          {/* AI Product Creation Modal */}
          {!props.isPublicView && (
            <AIProductCreationModal
              open={aiProductModalOpen}
              onOpenChange={setAiProductModalOpen}
              onSuccess={() => {
                // Optionally refetch products or show success message
                setAiProductModalOpen(false);
              }}
            />
          )}

          {/* Placement Mode Indicator - Shows current item and count when in placement mode */}
          {!props.isPublicView && paletteCurrentItem && (
            <PlacementModeIndicator
              currentItem={paletteCurrentItem}
              placementCount={palettePlacementCount}
              onClear={handleClearPalettePlacement}
            />
          )}
        </div>
      </FloorPlanProvider>
    </FloorPlanErrorBoundary>
  );
}

// Export as default for lazy loading
export default FloorPlanViewerV2;
